import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { notifyError, notifySuccess } from "../../effects-bridge";
import {
  confirmEnrolment,
  optOutOfLoyaltyProgram,
  refreshCustomerLoyalty,
  requestEnrolment,
} from "../index";
import {
  buildProviderConsents,
  getConsentEnforcement,
  getConsentForProvider,
  isConsentRequired,
  isPreConsentCustomer,
  resolveEnrolToggle,
} from "../consent";
import { useIsConsentMandatory } from "./useIsConsentMandatory";
import { useEnsureProgramDetails } from "./useProgramDetailsFetch";
import { hasProgram, skipsEnrollmentOtp } from "../program";
import {
  selectActiveLoyaltyProvider,
  selectBusinessData,
  selectCustomerProviderConsents,
  selectCustomerRecordKey,
  selectIsOffline,
  selectLoyaltyProgramDetails,
} from "../selectors";

/** What the hosting screen has to tell the hook about itself. */
export interface CustomerConsentOptions {
  /** Do we hold a DB record for this customer? A new customer answers `false`. */
  hasCustomerRecord: boolean;
  /** Is the customer step actually on screen? Consent is only enforced while it is. */
  isCustomerStepVisible: boolean;
  /** The phone number to enrol, read at call time — a form value, not a render value. */
  getPhoneNumber: () => string;
  /**
   * The customer payload to enrol from, read at call time.
   *
   * ⚠️ Load-bearing on the OTP-less and verify calls: no other request carries the customer, so
   * this is what the backend creates and enrols them from.
   */
  getCustomerPayload: () => any;
}

/**
 * Loyalty enrolment consent for a customer, headless.
 *
 * Everything the customer modal used to do inline — the mandatory check, the enrol toggle and its
 * seeding, the two enrolment paths, the OTP dialog's lifecycle, the toasts, and the
 * `providerConsents` map that goes on the customer save. A screen keeps only its markup and its
 * own submit wiring.
 *
 * ```tsx
 * const consent = useCustomerConsent({ hasCustomerRecord, isCustomerStepVisible, getPhoneNumber, getCustomerPayload });
 *
 * // gate the customer-step CTA
 * <Cta disabled={consent.enforcement.blocksSubmit} onClick={() => consent.runWithConsent(save)} />
 * ```
 *
 * See `./index.ts` for the layering and `docs/flows/LOYALTY.md` for the two paths.
 */
export function useCustomerConsent({
  hasCustomerRecord,
  isCustomerStepVisible,
  getPhoneNumber,
  getCustomerPayload,
}: CustomerConsentOptions) {
  const businessData = useSelector(selectBusinessData);
  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const programDetails = useSelector(selectLoyaltyProgramDetails);
  const providerConsents = useSelector(selectCustomerProviderConsents);
  const customerRecordKey = useSelector(selectCustomerRecordKey);
  const isOffline = useSelector(selectIsOffline);

  const providerMetaId = activeProvider?.metaId;
  /** `customerConsentDefaultOff` is stored inverted — the merchant opts OUT of the default. */
  const defaultOn = !businessData?.customerConsentDefaultOff;
  /**
   * dotpe + the programme's `skipEnrollmentOtp` — no OTP friction to opt out of, so
   * `resolveEnrolToggle` seeds the toggle on over the merchant's default. Never over a decline
   * recorded for a customer we hold: that one still wins (see `resolveEnrolToggle`).
   *
   * It reads the programme, so it is `false` until `useEnsureProgramDetails` below has one; the
   * seeding effect lists it and re-seeds when it lands.
   */
  const skipsOtp = skipsEnrollmentOtp(providerMetaId, programDetails);

  /**
   * 🔴 **The programme is this flow's own dependency, so the flow fetches it.** `isMandatory` (for
   * a partner that needs a live programme), `enforcement` (`skipsEnrollmentOtp`) and
   * `showsProgramCta` all read it, and the only other fetch is each app's `NewOrder` — which mobile
   * opens AFTER its customer step. A host must not have to know that; see
   * `useEnsureProgramDetails`.
   */
  useEnsureProgramDetails(isCustomerStepVisible);
  const isMandatory = useIsConsentMandatory();

  const [enrolCustomer, setEnrolCustomer] = useState(
    resolveEnrolToggle({ providerConsents, providerMetaId, hasCustomerRecord, defaultOn, skipsOtp }),
  );
  const [consentProvidedNow, setConsentProvidedNow] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  /** A different customer is a different answer — never inherit the last one's consent. */
  useEffect(() => {
    setConsentProvidedNow(false);
  }, [customerRecordKey]);

  /** Seed the toggle from what we know about this customer. */
  useEffect(() => {
    setEnrolCustomer(
      resolveEnrolToggle({ providerConsents, providerMetaId, hasCustomerRecord, defaultOn, skipsOtp }),
    );
  }, [providerConsents, providerMetaId, hasCustomerRecord, defaultOn, skipsOtp]);

  const consentGivenForProvider = getConsentForProvider(providerConsents, providerMetaId);
  const consentRequired = isConsentRequired({
    isMandatory,
    hasCustomerRecord,
    providerConsents,
    providerMetaId,
    enrolCustomer,
    consentProvidedNow,
    isCustomerStepVisible,
    isOffline,
  });
  const enforcement = getConsentEnforcement(consentRequired, providerMetaId, programDetails);

  /**
   * Record consent locally. The provider re-read is NOT here any more — `requestEnrolment` and
   * `confirmEnrolment` fire it themselves on every terminal success, so the two surfaces cannot
   * disagree about when `externalLoyaltyData` is refreshed.
   *
   * 🔴 **It closes the OTP dialog too, and that belongs HERE rather than in the branches.**
   * Settled consent and an open OTP dialog are contradictory states: the block has already
   * re-rendered as "Customer is now enrolled", and the dialog is asking for a code that can no
   * longer verify anything. Every terminal success routes through this one function, so the
   * invariant holds for all three of them and a fourth branch cannot forget it — a resend that
   * comes back `already-consented` used to leave the dialog stranded exactly that way.
   *
   * The dialog still calls `onClose` itself after a successful verify. That is its own contract
   * with its host and should not depend on what the hook does; both closing it is idempotent.
   */
  const markConsented = useCallback(() => {
    setConsentProvidedNow(true);
    setIsOtpModalOpen(false);
  }, []);

  /**
   * Ask the backend for an enrolment OTP. One request; `isResend` picks the toast.
   *
   * 🔴 **Deliberately NOT returned, and the two wrappers below take no arguments at all.** This
   * was one exported `sendOtp(isResend = false)`, and the "Send OTP" CTA was wired
   * `onPress={consent.sendOtp}` — so `Pressable`'s `GestureResponderEvent` (a synthetic click
   * under react-native-web) landed in `isResend` as a TRUTHY value, the resend branch ran, and
   * the dialog never opened. The OTP itself went out, which is exactly what made it read as a
   * rendering bug rather than a wiring one, and it broke both platforms at once.
   *
   * `tsc` cannot catch that: an optional parameter makes `(isResend?: boolean) => void`
   * assignable to `ActionButton`'s `onPress: () => void`, so the CTA type-checked clean. The
   * only fix that holds is having no parameter for an event to occupy — which is the shape
   * `useRedeemPointsOtp` already uses for its own `verify` / `resend` pair.
   */
  const requestOtp = useCallback(
    (isResend: boolean) => {
      requestEnrolment(
        { phoneNumber: getPhoneNumber(), provider: activeProvider, viaOtp: true },
        (outcome) => {
          switch (outcome) {
            case "already-consented":
              notifySuccess("Customer consent already provided.");
              markConsented();
              break;
            case "otp-required":
              // Opened on BOTH paths. A resend is raised from inside the open dialog, so this is
              // a no-op there — but it means no route can put a code in the customer's hands
              // with nowhere to type it, which is the failure this replaced.
              setIsOtpModalOpen(true);
              notifySuccess(isResend ? "OTP resent." : "OTP sent successfully.");
              break;
            // `enrolled` cannot reach here: on the OTP path a plain success means the code went
            // out. Handled as a failure rather than silently, so a backend change is visible.
            default:
              notifyError("OTP sending failed.");
          }
        },
      );
    },
    [activeProvider, getPhoneNumber, markConsented],
  );

  /** The "Send OTP" CTA. Opens the dialog on success. */
  const sendOtp = useCallback(() => requestOtp(false), [requestOtp]);

  /** The dialog's own "Resend OTP". Same request, different toast. */
  const resendOtp = useCallback(() => requestOtp(true), [requestOtp]);

  /** Verify the code the cashier typed. `cb(success)` lets the dialog show its own error state. */
  const verifyOtp = useCallback(
    (otp: string, cb: (success: boolean) => void) => {
      confirmEnrolment(
        {
          phoneNumber: getPhoneNumber(),
          provider: activeProvider,
          customerInfo: getCustomerPayload(),
        },
        otp,
        (outcome) => {
          if (outcome === "failed") {
            notifyError("OTP verification failed.");
            cb(false);
            return;
          }
          notifySuccess(
            outcome === "already-consented"
              ? "Customer consent already provided."
              : "OTP verification successful.",
          );
          markConsented();
          cb(true);
        },
      );
    },
    [activeProvider, getPhoneNumber, getCustomerPayload, markConsented],
  );

  /**
   * Enrol without an OTP, then continue.
   *
   * ⚠️ The backend can still decide an OTP is needed — `otp-sent` — in which case the dialog opens
   * and `next` is NOT run. Holding it there is the point: an enrolment that has not happened must
   * not advance the screen.
   */
  const enrolThenContinue = useCallback(
    (next: () => void) => {
      requestEnrolment(
        {
          phoneNumber: getPhoneNumber(),
          provider: activeProvider,
          customerInfo: getCustomerPayload(),
          viaOtp: false,
        },
        (outcome) => {
          switch (outcome) {
            case "otp-required":
              setIsOtpModalOpen(true);
              notifySuccess("OTP sent successfully.");
              break;
            case "enrolled":
            case "already-consented":
              markConsented();
              next();
              break;
            default:
              notifyError("Customer enrollment failed.");
          }
        },
      );
    },
    [activeProvider, getPhoneNumber, getCustomerPayload, markConsented],
  );

  /**
   * Run a screen action, enrolling first when this programme enrols on the action rather than
   * behind an OTP.
   *
   * `next` receives `true` when enrolment just happened, because the caller's save has to carry
   * the flag explicitly — enrolment and the save land in the same tick, so reading the hook's
   * state there would still see the stale `false`.
   */
  const runWithConsent = useCallback(
    (next: (consentProvided?: boolean) => void) => {
      if (enforcement.enrolOnNext) {
        enrolThenContinue(() => next(true));
        return;
      }
      next();
    },
    [enforcement.enrolOnNext, enrolThenContinue],
  );

  /** The `providerConsents` map to send with the customer save. */
  const buildConsentsForSave = useCallback(
    (consentProvided: boolean = consentProvidedNow) =>
      buildProviderConsents({
        existing: providerConsents,
        providerMetaId,
        isNewCustomer: !hasCustomerRecord,
        consentProvided,
        applies: isMandatory && !isOffline,
      }),
    [providerConsents, providerMetaId, hasCustomerRecord, consentProvidedNow, isMandatory, isOffline],
  );

  return {
    /** Is consent in play for this merchant, provider and customer at all? */
    isMandatory,
    /** A customer who predates the feature — never prompted. */
    isPreConsentCustomer: isPreConsentCustomer(hasCustomerRecord, providerConsents),
    /** Already enrolled, whether recorded earlier or just now. */
    isEnrolled: !!consentGivenForProvider || consentProvidedNow,
    consentProvidedNow,
    /** Who enforces consent: `blocksSubmit`, `enrolOnNext`, `showsSendOtpCta`. */
    enforcement,
    isOffline,
    /** Whether a "View program details" affordance has anything to show. */
    showsProgramCta: hasProgram(programDetails),

    enrolCustomer,
    setEnrolCustomer,
    toggleEnrolCustomer: useCallback(() => setEnrolCustomer((prev) => !prev), []),

    isOtpModalOpen,
    openOtpModal: useCallback(() => setIsOtpModalOpen(true), []),
    closeOtpModal: useCallback(() => setIsOtpModalOpen(false), []),

    sendOtp,
    resendOtp,
    verifyOtp,
    enrolThenContinue,
    runWithConsent,
    buildConsentsForSave,
  };
}

/**
 * Withdrawing a customer from the programme ("Leave program").
 *
 * Separate from `useCustomerConsent` on purpose: it is used from the customer summary, which has
 * no form, no enrol toggle and no OTP — only a confirmation and a phone number.
 */
export function useLoyaltyOptOut() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const optOut = useCallback((customerPhone: string, onDone?: (success: boolean) => void) => {
    setIsSubmitting(true);
    optOutOfLoyaltyProgram(customerPhone, (res: any) => {
      setIsSubmitting(false);
      const success = !!res?.status;
      if (success) {
        notifySuccess("Customer opted out successfully.");
        // Re-read rather than patching consent locally: opting out changes points and enrolment
        // together, and the provider is the authority on both.
        refreshCustomerLoyalty(customerPhone);
      } else {
        notifyError("User opted out fail");
      }
      onDone?.(success);
    });
  }, []);

  return { optOut, isSubmitting };
}
